import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { groupsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Users, Globe, Lock, AlertCircle, Upload, X } from 'lucide-react';

const CATEGORIES = [
  { value: 'alcool', label: 'Álcool' },
  { value: 'nicotina', label: 'Nicotina' },
  { value: 'drogas', label: 'Drogas' },
  { value: 'emocoes', label: 'Emoções' },
  { value: 'jogos-apostas', label: 'Jogos e Apostas' },
  { value: 'espiritualidade', label: 'Espiritualidade' },
  { value: 'geral', label: 'Geral' },
];

export function GroupCreate() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (name.length < 3 || name.length > 60) {
      newErrors.name = 'Nome deve ter entre 3 e 60 caracteres';
    }

    // Category validation
    if (!category) {
      newErrors.category = 'Categoria é obrigatória';
    }

    // Description validation (optional but has max length)
    if (description.length > 400) {
      newErrors.description = 'Descrição não pode ter mais de 400 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato inválido. Use JPG ou PNG');
      return;
    }

    // Validate file size (3MB max)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 3MB');
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setCoverPreview(previewUrl);

    // TODO: Upload to server and get URL
    // For now, just set a placeholder
    setCoverUrl(previewUrl);
    toast.info('Preview de capa carregado (upload será implementado)');
  };

  const removeCoverPreview = () => {
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
      setCoverPreview(null);
    }
    setCoverUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      const groupData = {
        name: name.trim(),
        category,
        description: description.trim(),
        visibility,
        rules: rules.trim() ? rules.trim().split('\n').filter(r => r.trim()) : [],
        cover_url: coverUrl || null,
      };

      const response = await groupsAPI.createGroup(groupData);
      toast.success('Grupo criado com sucesso!');
      
      // Redirect to group detail
      navigate(`/grupos/${response.data.slug}`);
    } catch (error) {
      console.error('Failed to create group:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao criar grupo';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const remainingChars = 400 - description.length;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="max-w-2xl mx-auto py-6 px-4" data-testid="group-create-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Criar Grupo</h1>
        <p className="text-muted-foreground">
          Crie um espaço para compartilhar experiências e apoiar outros membros
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Cover Image */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Imagem de Capa</CardTitle>
            <CardDescription>
              Escolha uma imagem JPG ou PNG de até 3MB (opcional)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {coverPreview ? (
              <div className="relative">
                <img
                  src={coverPreview}
                  alt="Preview da capa"
                  className="w-full h-48 object-cover rounded-lg"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={removeCoverPreview}
                  data-testid="remove-cover-button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleCoverChange}
                  className="hidden"
                  data-testid="cover-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCover}
                  data-testid="upload-cover-button"
                >
                  {uploadingCover ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Escolher Imagem'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
            <CardDescription>
              Detalhes principais sobre o grupo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">
                Nome <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Grupo de Apoio Álcool"
                maxLength={60}
                data-testid="name-input"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.name}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {name.length}/60 caracteres
              </p>
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="category">
                Categoria <span className="text-red-500">*</span>
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category" data-testid="category-select" aria-invalid={!!errors.category}>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.category}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva brevemente sobre o que é o grupo..."
                className={`min-h-[100px] resize-none ${isOverLimit ? 'border-red-500' : ''}`}
                data-testid="description-textarea"
                aria-invalid={!!errors.description}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.description && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.description}
                  </p>
                )}
                <div className="flex-1" />
                <p
                  className={`text-sm ${
                    isOverLimit
                      ? 'text-red-500'
                      : remainingChars < 80
                      ? 'text-yellow-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  {remainingChars} caracteres restantes
                </p>
              </div>
            </div>

            {/* Rules */}
            <div>
              <Label htmlFor="rules">Regras do Grupo (opcional)</Label>
              <Textarea
                id="rules"
                value={rules}
                onChange={(e) => setRules(e.target.value)}
                placeholder="Uma regra por linha...\nEx: Respeito mútuo\nSem spam\nConfidencialidade"
                className="min-h-[100px] resize-none"
                data-testid="rules-textarea"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Digite cada regra em uma nova linha
              </p>
            </div>

            {/* Visibility */}
            <div>
              <Label>Visibilidade</Label>
              <RadioGroup value={visibility} onValueChange={setVisibility}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="public" id="public" />
                  <Label htmlFor="public" className="flex items-center gap-2 font-normal cursor-pointer">
                    <Globe className="h-4 w-4" />
                    Público - Qualquer um pode ver e participar
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="private" id="private" />
                  <Label htmlFor="private" className="flex items-center gap-2 font-normal cursor-pointer">
                    <Lock className="h-4 w-4" />
                    Privado - Apenas convidados podem participar
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/grupos')}
            disabled={loading}
            data-testid="cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || isOverLimit}
            data-testid="submit-button"
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              'Criar Grupo'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
